import { useApiClient } from '@yuksalish/api-client'
import type {
  DailyOutput,
  DailyOutputRow,
  DowntimeEvent,
  DowntimeReason,
  MachineAssignment,
  MachineCounter,
  MachineEfficiencyRow,
  MachineFault,
  MachineStatus,
  MaintenanceRequest,
  MaterialRequirement,
  OperatorOrder,
  OperatorStage,
  SetupParameter,
} from '../types/operator'

export interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

export interface ApiItem<T> {
  data: T
}

export interface PagedResult<T> {
  items: T[]
  meta: { page: number; per_page: number; total: number; total_pages: number }
}

function parseMeta(meta: ApiList<never>['meta'], fallbackPage = 1, fallbackPerPage = 20) {
  return {
    page: meta?.page ?? meta?.current_page ?? fallbackPage,
    per_page: meta?.per_page ?? fallbackPerPage,
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

export function useOperatorApi() {
  const client = useApiClient()

  async function listMachines(filters: {
    status?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<MachineStatus>> {
    const response = await client.get<ApiList<MachineStatus>>('/production-execution/machines', {
      params: {
        status: filters.status || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function listAssignments(filters: {
    machine_id?: number
    type?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<MachineAssignment>> {
    const response = await client.get<ApiList<MachineAssignment>>('/production-execution/assignments', {
      params: {
        machine_id: filters.machine_id,
        type: filters.type || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function getOrder(orderId: number): Promise<OperatorOrder> {
    const response = await client.get<ApiItem<OperatorOrder>>(`/production-orders/${orderId}`)
    return response.data.data
  }

  async function listStages(orderId: number): Promise<OperatorStage[]> {
    const response = await client.get<ApiList<OperatorStage>>(`/production-orders/${orderId}/stages`)
    return response.data.data
  }

  async function listDowntimeReasons(): Promise<DowntimeReason[]> {
    const response = await client.get<ApiList<DowntimeReason>>('/production-execution/downtime-reasons')
    return response.data.data
  }

  async function openDowntime(payload: {
    reason_code: string
    machine_id?: number | null
    order_id?: number | null
    lot_number?: string | null
    start_time: string
    notes?: string
  }): Promise<DowntimeEvent> {
    const response = await client.post<ApiItem<DowntimeEvent>>('/production-execution/downtime', payload)
    return response.data.data
  }

  async function closeDowntime(id: number, payload: { end_time: string; notes?: string }): Promise<DowntimeEvent> {
    const response = await client.post<ApiItem<DowntimeEvent>>(`/production-execution/downtime/${id}/close`, payload)
    return response.data.data
  }

  async function listDowntime(filters: {
    machine_id?: number
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<DowntimeEvent>> {
    const response = await client.get<ApiList<DowntimeEvent>>('/production-execution/downtime', {
      params: {
        machine_id: filters.machine_id,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function fetchDailyOutput(date: string): Promise<DailyOutput> {
    const response = await client.get<ApiItem<DailyOutput>>('/production-execution/daily-output', {
      params: { date },
    })
    return response.data.data
  }

  // --- Machine operations endpoints ---

  async function getMachineSetup(machineId: number, params: {
    order_id?: number
    lot_number?: string
  } = {}): Promise<SetupParameter[]> {
    const response = await client.get<ApiList<SetupParameter>>(`/production-execution/machines/${machineId}/setup`, {
      params: { order_id: params.order_id, lot_number: params.lot_number || undefined },
    })
    return response.data.data
  }

  async function saveMachineSetup(
    machineId: number,
    payload: {
      order_id: number
      lot_number?: string | null
      parameters: Array<{ code: string; value: string }>
      notes?: string
    },
  ): Promise<SetupParameter[]> {
    const response = await client.put<ApiList<SetupParameter>>(`/production-execution/machines/${machineId}/setup`, payload)
    return response.data.data
  }

  async function getMachineRequirements(machineId: number, orderId?: number): Promise<MaterialRequirement[]> {
    const response = await client.get<ApiList<MaterialRequirement>>(`/production-execution/machines/${machineId}/requirements`, {
      params: { order_id: orderId },
    })
    return response.data.data
  }

  async function listMachineFaults(machineId: number, filters: {
    status?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<MachineFault>> {
    const response = await client.get<ApiList<MachineFault>>(`/production-execution/machines/${machineId}/faults`, {
      params: {
        status: filters.status || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function reportMachineFault(
    machineId: number,
    payload: {
      fault_code: string
      description: string
      order_id?: number | null
      lot_number?: string | null
    },
  ): Promise<MachineFault> {
    const response = await client.post<ApiItem<MachineFault>>(`/production-execution/machines/${machineId}/faults`, payload)
    return response.data.data
  }

  async function resolveMachineFault(
    machineId: number,
    faultId: number,
    payload: { resolution_notes?: string },
  ): Promise<MachineFault> {
    const response = await client.post<ApiItem<MachineFault>>(
      `/production-execution/machines/${machineId}/faults/${faultId}/resolve`,
      payload,
    )
    return response.data.data
  }

  async function listMachineMaintenance(machineId: number, filters: {
    status?: string
    page?: number
    per_page?: number
  } = {}): Promise<PagedResult<MaintenanceRequest>> {
    const response = await client.get<ApiList<MaintenanceRequest>>(`/production-execution/machines/${machineId}/maintenance`, {
      params: {
        status: filters.status || undefined,
        page: filters.page,
        per_page: filters.per_page,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function requestMachineMaintenance(
    machineId: number,
    payload: { reason: string; priority: string; notes?: string },
  ): Promise<MaintenanceRequest> {
    const response = await client.post<ApiItem<MaintenanceRequest>>(`/production-execution/machines/${machineId}/maintenance`, payload)
    return response.data.data
  }

  async function updateMaintenanceStatus(
    id: number,
    payload: { status: string; notes?: string },
  ): Promise<MaintenanceRequest> {
    const response = await client.post<ApiItem<MaintenanceRequest>>(`/production-execution/maintenance/${id}/status`, payload)
    return response.data.data
  }

  async function recordMachineCounter(
    machineId: number,
    payload: {
      counter_total: number
      counter_shift: number
      recorded_at: string
      client_event_id: string
    },
  ): Promise<MachineCounter> {
    const response = await client.post<ApiItem<MachineCounter>>(`/production-execution/machines/${machineId}/counters`, payload)
    return response.data.data
  }

  async function getMachineEfficiency(machineId: number, params: {
    from: string
    to: string
    group_by?: string
  }): Promise<MachineEfficiencyRow[]> {
    const response = await client.get<ApiList<MachineEfficiencyRow>>(`/production-execution/machines/${machineId}/efficiency`, {
      params: {
        from: params.from,
        to: params.to,
        group_by: params.group_by || 'stage',
      },
    })
    return response.data.data
  }

  function summarizeRows(rows: DailyOutputRow[]) {
    return rows.reduce(
      (acc, row) => ({
        completed: acc.completed + Number(row.completed_quantity || 0),
        rejected: acc.rejected + Number(row.rejected_quantity || 0),
        rework: acc.rework + Number(row.rework_quantity || 0),
      }),
      { completed: 0, rejected: 0, rework: 0 },
    )
  }

  return {
    listMachines,
    listAssignments,
    getOrder,
    listStages,
    listDowntimeReasons,
    openDowntime,
    closeDowntime,
    listDowntime,
    fetchDailyOutput,
    summarizeRows,
    getMachineSetup,
    saveMachineSetup,
    getMachineRequirements,
    listMachineFaults,
    reportMachineFault,
    resolveMachineFault,
    listMachineMaintenance,
    requestMachineMaintenance,
    updateMaintenanceStatus,
    recordMachineCounter,
    getMachineEfficiency,
  }
}

export type OperatorApi = ReturnType<typeof useOperatorApi>
